import { EventToolbox } from 'client/utils/event-toolbox';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { TrackingConstant } from 'client/tracking/constant';
import { randomInt } from 'client/utils/seed-randomizers';
import { INVENTORY_PAGE_CUSTOM_EVENTS } from 'client/site-modules/shared/constants/inventory/page-events';
import {
  TrackingPayloadBuilder,
  getFirstVehicle,
} from 'client/engagement-handlers/inventory-engagement-handler/tracking-payload-builder';
import { ACTIONS, VIN_OVERVIEW_ID } from './constants';

/**
 * Handles inventory list view event.
 * Single pipeline: event → builder → fire.
 * @param {CustomEvent} event
 */
function fireInventoryListView({ detail }) {
  const { inventory, creativeId, radius, defaults, totalCount, attributes, photo360, isNational, ...additionalParams } =
    detail;

  const useTotalPriceChange = !!creativeId?.includes(VIN_OVERVIEW_ID);

  const trackingData = new TrackingPayloadBuilder(inventory)
    .withSummary(totalCount, attributes)
    .withIdentity()
    .withPrice()
    .withRecommendedOffer()
    .withBelowMarket()
    .withDealer()
    .withDeals()
    .withPhotos(photo360, defaults)
    .withDistance()
    .withDelivery(isNational, radius)
    .withHistory()
    .applyIf(useTotalPriceChange, builder => builder.withTotalPriceChange())
    .applyIf(!useTotalPriceChange, builder => builder.withPriceDrop())
    .build();

  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: {
      action_name: 'list',
      action_cause: 'page_load',
      action_category: 'system',
      creative_id: creativeId,
      ...trackingData,
      radius,
      ...additionalParams,
    },
    first_vehicle: getFirstVehicle(inventory),
  });
}

/**
 * Fires price promise price display tracking pixel.
 * @param {object} target
 * @param {number} price
 */
export function firePricePromisePriceDisplayTracking({ target, detail: { price } }) {
  const eventData = {
    action_name: TrackingConstant.ACTION_SHOW_CONTENT,
    subaction_name: TrackingConstant.SHOW_DEALER_PRICE,
    action_cause: 'page_load',
    action_category: 'system',
    creative_id: getCreativeId(target),
    value: price,
  };

  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: eventData,
  });
}

/**
 * Fires drawer open.
 * @param {string} action
 * @param {string} subaction
 * @param {string} creativeId
 * @param {string} [eventType=action_completed]
 * @param {boolean} [isLink=false]
 * @param {string|null} [value=null]
 */
export function fireDrawerOpen(
  action,
  subaction,
  creativeId,
  eventType = 'action_completed',
  isLink = false,
  value = null
) {
  const eventData = {
    action_name: action,
    action_cause: isLink ? 'link_click' : 'button_click',
    action_category: 'user',
    subaction_name: subaction,
    creative_id: creativeId,
    value,
  };

  EventToolbox.fireTrackAction({
    event_type: eventType,
    event_data: eventData,
  });
}

export function fireCpoPanelView({ detail: { creativeId } }) {
  const eventData = {
    action_name: TrackingConstant.ACTION_SHOW_CONTENT,
    subaction_name: TrackingConstant.SHOW_CPO_PANEL,
    action_cause: 'page_load',
    action_category: 'system',
    creative_id: creativeId,
  };

  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: eventData,
  });
}

export function fireCpoLabelView({ detail: { creativeId } }) {
  const eventData = {
    action_name: TrackingConstant.ACTION_SHOW_CONTENT,
    subaction_name: TrackingConstant.SHOW_CPO_LABEL,
    action_cause: 'page_load',
    action_category: 'system',
    creative_id: creativeId,
    uniq: randomInt(),
  };

  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: eventData,
  });
}

export function fireWidgetOpen({
  pageName,
  eventType = TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
  creativeId = TrackingConstant.CREATIVE_ID_VDP_INSTANT_CACHE_OFFER,
  actionCause = TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
  actionName = TrackingConstant.ACTION_WIDGET_VIEW,
  synpartner = TrackingConstant.SYNPARTNER,
}) {
  const eventData = {
    creative_id: creativeId,
    action_cause: actionCause,
    action_name: actionName,
    action_category: 'user',
  };

  EventToolbox.fireTrackAction({
    page_name: pageName,
    event_type: eventType,
    event_data: eventData,
    synpartner,
  });
}

export function fireDealCheckView({ detail: { creativeId } }) {
  const pageHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
    event_data: {
      action_name: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
      action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
      total_page_height: pageHeight,
      creative_id: creativeId,
    },
  });
}

export function fireOpenTed({ detail: { isPurchase } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_VIEW_PRICING_DETAILS,
      subaction_name: isPurchase ? TrackingConstant.VIEW_LOAN_DETAILS : TrackingConstant.VIEW_LEASE_DETAILS,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      creative_id: 'digital-retail-entry-module',
      value: 'Get Started',
    },
  });
}

export function fireBuyOnlineSrpCardBtnClick({ detail: { partnerId } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_VIEW_CONTENT,
      subaction_name: TrackingConstant.BUY_ONLINE_CLICKOUT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      creative_id: 'bol-srp-card',
    },
    partner_id: partnerId,
  });
}

export function fireVdpPricinBoxBtnClick(
  { detail: { isMobile, value, partnerId, type = 'used' } } = { detail: { isMobile: false } }
) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_VIEW_CONTENT,
      subaction_name: TrackingConstant.BUY_ONLINE_CLICKOUT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      creative_id: isMobile
        ? `${type}-buy_online_cta_inline-${type}_model_car_inventory_vin_detail`
        : `${type}-buy_online_cta-${type}_model_car_inventory_vin_detail`,
      value,
    },
    partner_id: partnerId,
  });
}

export function fireVdpEmbeddedBoxBtnClick({ detail: { value, type, partnerId } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_VIEW_CONTENT,
      subaction_name: TrackingConstant.BUY_ONLINE_CLICKOUT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      creative_id: `${type}-buy_online_cta_embedded-${type}_model_car_inventory_vin_detail`,
      value,
    },
    partner_id: partnerId,
  });
}

export function fireToggleDeliveryFees({ detail: { isIncluded } }) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.VIEW_SEARCH_RESULT,
      subaction_name: TrackingConstant.FILTER_SEARCH_RESULT,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      creative_id: 'edm-entry-facets-buying-experience',
      value: `IncludeDeliveryFees~${isIncluded ? 'true' : 'false'}~Include Delivery Fees in Prices`,
    },
  });
}

export const InventoryEngagementHandler = {
  init() {
    EventToolbox.on(ACTIONS.INVENTORY_LIST_VIEW, event => fireInventoryListView(event));
    EventToolbox.on(ACTIONS.PRICE_PROMISE_PRICE_DISPLAY, firePricePromisePriceDisplayTracking);
    EventToolbox.on(ACTIONS.CPO_PANEL_VIEW, fireCpoPanelView);
    EventToolbox.on(ACTIONS.CPO_LABEL_VIEW, fireCpoLabelView);
    EventToolbox.on(ACTIONS.DEAL_CHECK_VIEW, fireDealCheckView);
    EventToolbox.on(INVENTORY_PAGE_CUSTOM_EVENTS.DIGITAL_RETAIL_ENTRY_OPEN_TED, fireOpenTed);
    EventToolbox.on(ACTIONS.BUY_ONLINE_SRP_CARD_BTN_CLICK, fireBuyOnlineSrpCardBtnClick);
    EventToolbox.on(ACTIONS.PRICING_BOX_BTN_CLICK, fireVdpPricinBoxBtnClick);
    EventToolbox.on(ACTIONS.EMBEDDED_BOX_BTN_CLICK, fireVdpEmbeddedBoxBtnClick);
    EventToolbox.on(ACTIONS.TOGGLE_DELIVERY_FEES, fireToggleDeliveryFees);
  },
};
